import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { useActivateProjectMutation, useGetProjectByIdQuery } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { clsx } from 'clsx';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const CreateProjectStep3Page = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(null);
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const notification = useNotification();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useAppDispatch();
  const [activateProject, { isLoading: isLoadingActivateProject }] = useActivateProjectMutation();

  useLoaderWhile(isLoadingActivateProject);

  useEffect(() => {
    if (currentProject && !currentProject.is_active) {
      activateProject({ projectId: id }).then((activateProjectResult) => {
        if (!('error' in activateProjectResult)) {
          notification.success('Project has been activated!');
        }
      });
    }
  }, [currentProject]);

  useEffect(() => {
    const config = creationFlowState[id];

    setValue(config?.promptType || 'system');

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          promptType: value,
        }),
      );
    }
  }, [creationFlowState]);

  useEffect(() => {
    if (value) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          promptType: value,
        }),
      );
    }
  }, [value]);

  const next = () => {
    navigate(getCreateProjectStepPath(id, +step + 1, projectTypeVar), { replace: true });
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      <CreateProjectStepsTitle step={+step} title={'AI Persona Setup Method'} />
      <CreateProjectHead step={+step} titleIndex={2} />
      <div className={'flex flex-col w-[455px]'}>
        <RadioGroup
          defaultValue={'system'}
          onValueChange={setValue}
          value={value}
          className="flex flex-col space-y-1"
        >
          <label
            htmlFor="system"
            className={'flex flex-col w-full gap-1.5 group/label hover:cursor-pointer'}
          >
            <div className={clsx('flex items-center space-x-5 space-y-0')}>
              <RadioGroupItem value={'system'} id="system" />
              <p className="font-medium text-base text-ebony opacity-70 group-hover/label:opacity-100 transition">
                Use System Prompt Builder 🏗️
              </p>
            </div>
            <div className={'pl-9'}>
              <p className={'text-ebony_opacity_50 text-sm font-medium'}>
                Create the AI system prompt through a guided, step-by-step flow with built-in best
                practices. Ideal if you want a fast, reliable setup.
              </p>
            </div>
          </label>

          <label
            htmlFor="manual"
            className={'flex flex-col w-full gap-1.5 group/label hover:cursor-pointer'}
          >
            <div className={clsx('flex items-center space-x-5 space-y-0')}>
              <RadioGroupItem value={'manual'} id="manual" />
              <p className="font-medium text-base text-ebony opacity-70 group-hover/label:opacity-100 transition">
                Write System Prompt Manually ✍️
              </p>
            </div>
            <div className={'pl-9'}>
              <p className={'text-ebony_opacity_50 text-sm font-medium'}>
                Start with a blank editor and craft the AI system prompt line by line. Ideal if you
                prefer full control and already know how to structure effective prompts.
              </p>
            </div>
          </label>
        </RadioGroup>
      </div>
      <div className={'w-[455px] flex flex-col gap-5 items-center'}>
        <Button onClick={next} className="min-w-24 w-full flex flex-row h-12">
          Continue
        </Button>
        <button
          type="button"
          onClick={back}
          className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
        >
          ← Back
        </button>
      </div>
    </PageContentWrapper>
  );
};

export default CreateProjectStep3Page;
